import React from 'react';
import { Stack, Text, useMantineTheme } from '@mantine/core';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	DropZone,
	HiddenInput,
	UploadLabel,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	promisedUploadFormat,
	renamedUploadKind,
	sniffUploadFormat,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FileUpload/FileUpload.utils';

interface Props {
	setUploadedFile: (file: File | null) => void;
	inputId: string;
	/** DOCX_MASTER_DOCUMENTS. Off, a Word file is refused here rather than uploaded and then
	 *  refused by the backend. */
	allowDocx?: boolean;
}

const PDF_MIME_TYPE = 'application/pdf';
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const FileUpload = ({ setUploadedFile, inputId, allowDocx = false }: Props) => {
	const tMipManage = useTranslationMipManage();
	const theme = useMantineTheme();

	const acceptedFileTypes = allowDocx ? '.pdf,.docx' : '.pdf';

	/**
	 * Refuses a file whose extension its own bytes contradict, before it is uploaded.
	 *
	 * `file.type` alone cannot see this: browsers derive it from the extension, so renaming a
	 * PDF to `.docx` makes it claim the Word MIME type. The upload then succeeds, the backend
	 * sniffs a PDF and takes the PDF path, and every copy an investor downloads is a PDF named
	 * `.docx` that Word refuses to open.
	 */
	const validateFile = async (file: File): Promise<boolean> => {
		const allowedTypes = allowDocx ? [PDF_MIME_TYPE, DOCX_MIME_TYPE] : [PDF_MIME_TYPE];

		if (!allowedTypes.includes(file.type)) {
			showToastV2({
				title: tMipManage('master_documents.text.upload_error'),
				message: tMipManage('master_documents.text.upload_error_message'),
				state: 'error',
			});
			return false;
		}

		const renamed = renamedUploadKind(
			promisedUploadFormat(file.name),
			await sniffUploadFormat(file)
		);
		if (renamed !== null) {
			showToastV2({
				title: tMipManage('master_documents.text.upload_error_renamed', {
					defaultValue: 'That file is not the format its name claims',
				}),
				message:
					renamed === 'pdf_named_docx'
						? tMipManage('master_documents.text.upload_error_renamed_pdf_as_docx', {
								defaultValue:
									'This is a PDF named .docx. Renaming a file does not convert it — upload a real Word document, or upload this one as a .pdf.',
							})
						: tMipManage('master_documents.text.upload_error_renamed_docx_as_pdf', {
								defaultValue:
									'This is a Word document named .pdf. Rename it .docx and upload it again.',
							}),
				state: 'error',
			});
			return false;
		}

		return true;
	};

	const handleFileUpload = async (
		fileOrEvent: File | React.ChangeEvent<HTMLInputElement> | null
	) => {
		let file: File | null = null;

		if (fileOrEvent === null) {
			file = null;
		} else if (
			'target' in fileOrEvent &&
			fileOrEvent.target.files &&
			fileOrEvent.target.files.length > 0
		) {
			file = fileOrEvent.target.files[0];
		} else if (fileOrEvent instanceof File) {
			file = fileOrEvent;
		}

		if (file && !(await validateFile(file))) {
			return;
		}

		setUploadedFile(file);
	};

	const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			await handleFileUpload(e.dataTransfer.files[0]);
		}
	};

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target;
		await handleFileUpload(e);
		// Cleared so a refused file can be picked again after it is fixed: the input only fires
		// onChange when its value CHANGES, and re-picking the same path does not change it.
		input.value = '';
	};

	const preventDefaultHandler = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	return (
		<DropZone
			$borderColor={theme.colors.gray[4]}
			onDragOver={preventDefaultHandler}
			onDragLeave={preventDefaultHandler}
			onDrop={handleDrop}
		>
			<HiddenInput
				type="file"
				id={inputId}
				onChange={handleChange}
				accept={acceptedFileTypes}
			/>
			<UploadLabel htmlFor={inputId}>
				<Stack gap="xs" align="start">
					<Text>
						<Text component="span" c="blue.6" fw={600}>
							{tMipManage('master_documents.text.upload_click')}
						</Text>{' '}
						{tMipManage('master_documents.text.upload_drag')}
					</Text>
					<Text size="sm" c="dimmed">
						{allowDocx
							? tMipManage('master_documents.text.upload_hint_pdf_or_docx', {
									defaultValue: 'PDF or Word (.docx)',
								})
							: tMipManage('master_documents.text.upload_error_message')}
					</Text>
				</Stack>
			</UploadLabel>
		</DropZone>
	);
};
